'use client';

import React, { useState, useCallback } from 'react';
import { Plus, Trash2, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';

export interface GradeEntry {
  id: string;
  gradeName: string;
  officialBags: number;
  dealerBags: number;
}

export interface VarietyEntry {
  id: string;
  varietyName: string;
  grades: GradeEntry[];
}

interface VarietyGradeBreakdownProps {
  totalLotBags: number;
  varieties: VarietyEntry[];
  onChange: (varieties: VarietyEntry[]) => void;
  activeView: 'official' | 'dealer';
  onViewChange: (view: 'official' | 'dealer') => void;
}

const DEFAULT_GRADES = ['Grade A', 'Grade B', 'Grade C'];

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

function createDefaultGrades(): GradeEntry[] {
  return DEFAULT_GRADES.map((g) => ({
    id: generateId(),
    gradeName: g,
    officialBags: 0,
    dealerBags: 0,
  }));
}

export function createDefaultVariety(name = ''): VarietyEntry {
  return {
    id: generateId(),
    varietyName: name,
    grades: createDefaultGrades(),
  };
}

export default function VarietyGradeBreakdown({
  totalLotBags,
  varieties,
  onChange,
  activeView,
  onViewChange,
}: VarietyGradeBreakdownProps) {
  const [collapsedVarieties, setCollapsedVarieties] = useState<Set<string>>(new Set());

  // ── Totals ──────────────────────────────────────────────────────────────────
  const totalVarietyBags = varieties.reduce((sum, v) =>
    sum + v.grades.reduce((gs, g) => gs + (activeView === 'official' ? g.officialBags : g.dealerBags), 0), 0);
  const difference = totalLotBags - totalVarietyBags;
  const isMatch = difference === 0;

  // ── Variety helpers ─────────────────────────────────────────────────────────
  const addVariety = () => {
    onChange([...varieties, createDefaultVariety()]);
  };

  const removeVariety = (vid: string) => {
    onChange(varieties.filter((v) => v.id !== vid));
  };

  const updateVarietyName = (vid: string, name: string) => {
    onChange(varieties.map((v) => (v.id === vid ? { ...v, varietyName: name } : v)));
  };

  const toggleCollapse = (vid: string) => {
    setCollapsedVarieties((prev) => {
      const next = new Set(prev);
      next.has(vid) ? next.delete(vid) : next.add(vid);
      return next;
    });
  };

  // ── Grade helpers ────────────────────────────────────────────────────────────
  const addGrade = (vid: string) => {
    onChange(
      varieties.map((v) =>
        v.id === vid
          ? { ...v, grades: [...v.grades, { id: generateId(), gradeName: '', officialBags: 0, dealerBags: 0 }] }
          : v
      )
    );
  };

  const removeGrade = (vid: string, gid: string) => {
    onChange(
      varieties.map((v) =>
        v.id === vid ? { ...v, grades: v.grades.filter((g) => g.id !== gid) } : v
      )
    );
  };

  const updateGrade = useCallback(
    (vid: string, gid: string, field: keyof GradeEntry, value: string | number) => {
      onChange(
        varieties.map((v) =>
          v.id === vid
            ? {
                ...v,
                grades: v.grades.map((g) =>
                  g.id === gid ? { ...g, [field]: value } : g
                ),
              }
            : v
        )
      );
    },
    [varieties, onChange]
  );

  const varietyTotal = (v: VarietyEntry) =>
    v.grades.reduce((s, g) => s + (activeView === 'official' ? g.officialBags : g.dealerBags), 0);

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-foreground">Variety &amp; Grade Breakdown</h3>
          <span className="text-2xs bg-danger/10 text-danger font-semibold px-2 py-0.5 rounded-full">Required</span>
        </div>
        {/* View toggle */}
        <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            onClick={() => onViewChange('official')}
            className={`text-2xs font-semibold px-2.5 py-1 rounded-md transition-all ${
              activeView === 'official' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Official
          </button>
          <button
            type="button"
            onClick={() => onViewChange('dealer')}
            className={`text-2xs font-semibold px-2.5 py-1 rounded-md transition-all ${
              activeView === 'dealer' ?'bg-card text-accent shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Dealer
          </button>
        </div>
      </div>

      {/* View label */}
      <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
        activeView === 'official' ?'bg-primary/5 text-primary border border-primary/20' :'bg-accent/5 text-accent border border-accent/20'
      }`}>
        <Info size={12} />
        {activeView === 'official' ? 'Official Distributor Grading — bags as per distributor records' : 'Dealer Verification Grading — bags as verified by dealer'}
      </div>

      {/* Variety cards */}
      {varieties.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
          <p className="text-sm text-muted-foreground">No varieties added yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add at least one variety to proceed.</p>
        </div>
      )}

      {varieties.map((variety, vIdx) => {
        const isCollapsed = collapsedVarieties.has(variety.id);
        const vTotal = varietyTotal(variety);
        return (
          <div key={variety.id} className="border border-border rounded-xl overflow-hidden">
            {/* Variety header */}
            <div className="flex items-center gap-2 bg-muted/30 px-3 py-2.5">
              <span className="text-xs font-bold text-muted-foreground w-5 text-center">{vIdx + 1}</span>
              <input
                type="text"
                value={variety.varietyName}
                onChange={(e) => updateVarietyName(variety.id, e.target.value)}
                placeholder="Variety name (e.g. Nasik Big, Export, Local)"
                className="flex-1 bg-transparent text-sm font-semibold text-foreground placeholder:text-muted-foreground/60 outline-none border-b border-transparent focus:border-primary/40 transition-colors py-0.5"
              />
              <span className="text-xs font-bold text-muted-foreground ml-2 whitespace-nowrap">
                {vTotal} bags
              </span>
              <button
                type="button"
                onClick={() => toggleCollapse(variety.id)}
                className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              </button>
              {varieties.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeVariety(variety.id)}
                  className="p-1 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>

            {/* Grade rows */}
            {!isCollapsed && (
              <div className="p-3 space-y-2">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_100px_28px] gap-2 px-1">
                  <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Grade Name</span>
                  <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Bags</span>
                  <span />
                </div>

                {variety.grades.map((grade) => (
                  <div key={grade.id} className="grid grid-cols-[1fr_100px_28px] gap-2 items-center">
                    <input
                      type="text"
                      value={grade.gradeName}
                      onChange={(e) => updateGrade(variety.id, grade.id, 'gradeName', e.target.value)}
                      placeholder="e.g. Grade A, Premium, Export"
                      className="input-field text-xs py-1.5"
                    />
                    <input
                      type="number"
                      min={0}
                      value={activeView === 'official' ? grade.officialBags || '' : grade.dealerBags || ''}
                      onChange={(e) => {
                        const val = Math.max(0, parseInt(e.target.value) || 0);
                        updateGrade(variety.id, grade.id, activeView === 'official' ? 'officialBags' : 'dealerBags', val);
                      }}
                      placeholder="0"
                      className="input-field text-xs py-1.5 text-right font-mono"
                    />
                    {variety.grades.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeGrade(variety.id, grade.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <span className="w-7" />
                    )}
                  </div>
                ))}

                {/* Add grade */}
                <button
                  type="button"
                  onClick={() => addGrade(variety.id)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium mt-1 transition-colors"
                >
                  <Plus size={12} />
                  Add Grade
                </button>

                {/* Variety subtotal */}
                <div className="flex items-center justify-between pt-2 border-t border-border/50 mt-2">
                  <span className="text-xs text-muted-foreground">Variety Total</span>
                  <span className="text-sm font-bold text-foreground font-mono">{vTotal} bags</span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add variety button */}
      <button
        type="button"
        onClick={addVariety}
        className="w-full border-2 border-dashed border-primary/30 rounded-xl py-3 flex items-center justify-center gap-2 text-primary hover:bg-primary/5 transition-colors text-sm font-semibold"
      >
        <Plus size={15} />
        Add Variety
      </button>

      {/* Visual Summary */}
      <div className={`rounded-xl p-4 border ${isMatch ? 'bg-accent/5 border-accent/20' : 'bg-warning/5 border-warning/20'}`}>
        <p className="text-xs font-bold text-foreground mb-3">Bag Count Summary</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Lot Bags</span>
            <span className="font-bold font-mono text-foreground">{totalLotBags}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Variety Bags</span>
            <span className="font-bold font-mono text-foreground">{totalVarietyBags}</span>
          </div>
          <div className="w-full h-px bg-border my-1" />
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-foreground">Difference</span>
            <span className={`font-bold font-mono ${isMatch ? 'text-accent' : 'text-warning'}`}>
              {difference > 0 ? '+' : ''}{difference} bags
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${isMatch ? 'border-accent/20' : 'border-warning/20'}`}>
          {isMatch ? (
            <>
              <CheckCircle size={14} className="text-accent flex-shrink-0" />
              <p className="text-xs font-semibold text-accent">Bag counts match — ready to save</p>
            </>
          ) : (
            <>
              <AlertTriangle size={14} className="text-warning flex-shrink-0" />
              <p className="text-xs font-semibold text-warning">
                {difference > 0
                  ? `${difference} bags unaccounted — assign them to a variety/grade`
                  : `${Math.abs(difference)} bags over-assigned — reduce grade counts`}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
